import { useRef, useState } from "react"
import { Button, Textarea } from "@ui/shad"
import { cn } from "@ui/utils"
import { Loader2, X } from "lucide-react"

import { BaseInlineInput } from "./base"
import { InlineInputProps } from "./props"
import { useInlineEdit } from "./use-inline-edit"

export interface InlineTextareaProps<T = string> extends InlineInputProps<T> {
  rows?: number
  minRows?: number
  maxRows?: number
  fillHeight?: boolean
}

export function InlineTextarea<T = string>({
  label,
  LabelIcon,
  hideLabel = false,
  size,
  placeholder = "",
  focusedPlaceholder,
  value: controlledValue,
  defaultValue,
  onChange,
  onSave,
  isSaving = false,
  disabled = false,
  inputClasses,
  validator,
  formatter,
  parser,
  clearable = true,
  maxLength,
  fixedLabelWidth,
  resetOnInvalid = true,
  resetOnEmpty = false,
  rows = 3,
  minRows = 2,
  maxRows = 10,
  fillHeight = false,
}: InlineTextareaProps<T>) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const { stringValue, setValue, isEmpty, isValid, isSubmitting, handleSubmit, handleKeyDown, handleClear } =
    useInlineEdit({
      value: controlledValue,
      defaultValue,
      onChange,
      onSubmit: onSave,
      validator,
      formatter,
      parser,
      ref: textareaRef,
      resetOnInvalid,
      resetOnEmpty,
      disableClickOutside: true,
    })

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Override the default Enter behavior for textarea
    if (e.key === "Enter" && e.metaKey) {
      // Cmd/Ctrl + Enter to submit
      e.preventDefault()
      handleSubmit()
    } else if (e.key === "Escape") {
      e.preventDefault()
      handleKeyDown(e)
    }
  }

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      size={size}
      fixedLabelWidth={fixedLabelWidth}
      alignLabel="start"
      className={fillHeight ? "h-full min-h-0 items-stretch [&>div]:min-h-0" : undefined}
    >
      <div className={cn("relative w-full", fillHeight && "h-full min-h-0")}>
        <Textarea
          ref={textareaRef}
          value={stringValue}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleTextareaKeyDown}
          onBlur={() => {
            setIsFocused(false)
            handleSubmit()
          }}
          onFocus={() => setIsFocused(true)}
          className={cn(
            "hover:bg-muted focus-visible:border-input min-h-[60px] w-full cursor-pointer resize-none border-1 border-transparent px-2 py-1.5 shadow-none focus-visible:shadow-sm",
            isEmpty ? "text-input-placeholder" : "",
            !isValid ? "border-destructive" : "",
            clearable && !isEmpty && isFocused && "pr-8",
            inputClasses
          )}
          placeholder={isFocused && focusedPlaceholder ? focusedPlaceholder : placeholder}
          disabled={disabled}
          maxLength={maxLength}
          rows={rows}
          style={
            fillHeight
              ? { height: "100%", minHeight: 0 }
              : { minHeight: `${minRows * 1.5}rem`, maxHeight: `${maxRows * 1.5}rem` }
          }
        />

        {isSaving || isSubmitting ? (
          <Loader2 className="text-icon absolute top-2 right-2 h-4 w-4 animate-spin" />
        ) : (
          clearable &&
          !isEmpty &&
          isFocused && (
            <Button
              variant="ghost"
              size="iconXs"
              onMouseDown={(e) => {
                e.preventDefault()
                handleClear()
              }}
              className="absolute top-1 right-1"
            >
              <X className="text-icon" />
            </Button>
          )
        )}
      </div>
    </BaseInlineInput>
  )
}
